/* eslint-disable no-console */
export namespace PipelineGlUtils {

  export function CreateGlShader(gl: WebGLRenderingContext, shaderString: string, shaderType: number): WebGLShader {
    // console.log(`PipelineGlUtils::CreateGlShader: Creating shader of type <${shaderType}>`);

    const shader = gl.createShader(shaderType);
    if (!shader) {
      throw new Error(`PipelineGlUtils::CreateGlShader: GL context active <${!gl.isContextLost()}>. ` +
        `Failed to create shader of type <${shaderType}>`);
    }

    gl.shaderSource(shader, shaderString);
    gl.compileShader(shader);

    // TODO: Hide under some sort of "debug/show logs" flag.
    // * Doesn't appear to exist on server gl package
    // const debugShadersExt = gl.getExtension("WEBGL_debug_shaders");
    // if(debugShadersExt) {
    //   const src = debugShadersExt.getTranslatedShaderSource(shader);
    //   console.log(`PipelineGlUtils::CreateGlShader: shader <${shaderType}> translated source:`, src);
    // }

    return shader;
  }

  export function CreateShaderProgram(gl: WebGLRenderingContext, vertShaderStr: string, fragShaderStr: string): WebGLProgram {
    const vertShader = PipelineGlUtils.CreateGlShader(gl, vertShaderStr, gl.VERTEX_SHADER);
    const fragShader = PipelineGlUtils.CreateGlShader(gl, fragShaderStr, gl.FRAGMENT_SHADER);

    const shaderProgram = gl.createProgram();

    if (!shaderProgram) {
      throw new Error(`PipelineGlUtils::CreateShaderProgram: GL context active <${!gl.isContextLost()}>. Failed to create gl shader program.`);
    }

    gl.attachShader(shaderProgram, vertShader);
    gl.attachShader(shaderProgram, fragShader);
    gl.linkProgram(shaderProgram);
    gl.validateProgram(shaderProgram);

    const linkStatus = gl.getProgramParameter(shaderProgram, gl.LINK_STATUS);
    // console.log(`PipelineGlUtils::CreateShaderProgram: linkStatus <${linkStatus}>`);
    if (!linkStatus) {
      const errorcode = gl.getError();
      const programInfoLog = gl.getProgramInfoLog(shaderProgram);
      const compileStatusVert = gl.getShaderParameter(vertShader, gl.COMPILE_STATUS);
      const compileStatusFrag = gl.getShaderParameter(fragShader, gl.COMPILE_STATUS);
      console.error(`PipelineGlUtils::CreateShaderProgram: linkStatus failed, gl error code <${errorcode}>`);
      console.error(`- linkStatus failed, could not initialise shaders. GL context active <${!gl.isContextLost()}>, programInfoLog:`, programInfoLog);
      console.error(`- compile status: vertex shader <${compileStatusVert}>, info log:`, gl.getShaderInfoLog(vertShader));
      console.error(`- compile status: fragment shader <${compileStatusFrag}>, info log:`, gl.getShaderInfoLog(fragShader));
      console.error(`- vertShader code:`, vertShaderStr);
      console.error(`- fragShader code:`, fragShaderStr);
      throw new Error(`- linkStatus error thrown, notes above`);
    }

    return shaderProgram;
  }

  export function EnableVertexAttributeArray(gl: WebGLRenderingContext, shaderProgram: WebGLProgram, shaderAttrName: string): number {
    const attrLoc = gl.getAttribLocation(shaderProgram, shaderAttrName);
    gl.enableVertexAttribArray(attrLoc);
    return attrLoc;
  }

  /**
   * Use to debug all available uniforms of given program.
   * @param gl
   * @param shaderProgram
   */
  export function GetUniformInfo(gl: WebGLRenderingContext, shaderProgram: WebGLProgram) {
    // console.log(`Getting all uniform info for shader program:`, gl.getProgramInfoLog(shaderProgram));

    let i = 0;
    let info: WebGLActiveInfo | null;
    do {
      info = gl.getActiveUniform(shaderProgram, i);

      console.log(`Uniform index <${i}>:`, info);
      i++;
    }
    while (info !== null);
  }

  export function GetUniformLocation(gl: WebGLRenderingContext, shaderProgram: WebGLProgram, shaderUniformName: string): WebGLUniformLocation {
    // GetUniformInfo(gl, shaderProgram);

    const location = gl.getUniformLocation(shaderProgram, shaderUniformName);
    if (location === null) {
      throw new Error(`PipelineGlUtils::GetUniformLocation: Got location <${location}> of shader uniform <${shaderUniformName}>`);
    }
    return location;
  }

  export function SetVertexAttribute(gl: WebGLRenderingContext, buffer: WebGLBuffer | null, attrLocation: number, itemSize: number) {
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.vertexAttribPointer(attrLocation, itemSize, gl.FLOAT, false, 0, 0);
  }

  /**
   *
   * @param gl
   * @param imgData
   * @param renderingContextBase gl.CLAMP_TO_EDGE || gl.REPEAT
   * @returns
   */
  export function CreateGlTextureByImageData(gl: WebGLRenderingContext, imgData: ImageData, renderingContextBase: number): WebGLTexture {
    const glTexture = CreateGlTextureInternal(gl);

    // gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imgData);

    SetTextureWrap(gl, renderingContextBase);
    SetTextureMagFilter(gl);
    SetTextureMinFilter(gl, false);
    UnBindTexture(gl);

    return glTexture;
  }

  /**
   *
   * @param gl
   * @param width
   * @param height
   * @param pixels Can pass pixel data, or null to make an empty texture
   * @returns
   */
  export function CreateGlTextureByPixels(gl: WebGLRenderingContext, width: number, height: number, pixels: ArrayBufferView | null): WebGLTexture {
    const glTexture = CreateGlTextureInternal(gl);

    // gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipY);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    SetTextureWrap(gl, gl.CLAMP_TO_EDGE);
    SetTextureMinFilter(gl, false);
    UnBindTexture(gl);

    return glTexture;
  }

  function CreateGlTextureInternal(gl: WebGLRenderingContext): WebGLTexture {
    if (!gl) {
      throw new Error(`gl must be defined. It was ${gl}`);
    }
    const glTexture = gl.createTexture();
    if (glTexture === null) {
      throw new Error(`PipelineUtils::CreateGlTextureInternal: Could not create empty gl texture`);
    }

    gl.bindTexture(gl.TEXTURE_2D, glTexture);

    return glTexture;
  }

  /**
   * ! WebGL 1 will ONLY accept power-of-two textures if gl.REPEAT needs to be used
   * @param renderingContextBase gl.CLAMP_TO_EDGE || gl.REPEAT
   */
  export function SetTextureWrap(gl: WebGLRenderingContext, renderingContextBase: number) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, renderingContextBase);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, renderingContextBase);
  }

  export function SetTextureMagFilter(gl: WebGLRenderingContext) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  /**
   * ! WebGL 1 will ONLY accept power-of-two textures if gl.generateMipmap needs to be used
   * @param generateMipmap
   */
  export function SetTextureMinFilter(gl: WebGLRenderingContext, generateMipmap: boolean) {
    if (generateMipmap) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_NEAREST);
      gl.generateMipmap(gl.TEXTURE_2D);
    }
    else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
  }

  export function UnBindTexture(gl: WebGLRenderingContext) {
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  /**
   * // With framebuffer bound, gl.drawElements will render to the texture instead of the canvas.
   * @param gl
   * @param targetTexture
   */
  export function CreateTextureBoundFrameBuffer(gl: WebGLRenderingContext, targetTexture: WebGLTexture): WebGLFramebuffer {
    const frameBuffer = gl.createFramebuffer();
    if (frameBuffer === null) {
      throw new Error(`PipelineUtils::CreateTextureBoundFrameBuffer: Could not create frame buffer`);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, targetTexture, 0);

    return frameBuffer;
  }

  export function UnBindFrameBuffer(gl: WebGLRenderingContext) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  export function SetTextureUniform(
    gl: WebGLRenderingContext,
    textureNumber: number,
    glTexture: WebGLTexture,
    location: WebGLUniformLocation | null,
    bufferXvalue: number
  ) {
    gl.activeTexture(textureNumber);
    gl.bindTexture(gl.TEXTURE_2D, glTexture);
    gl.uniform1i(location, bufferXvalue);
  }
}
